import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { FormattedMessage } from 'react-intl'
import { Button, If, Text } from 'src/Components'
import { pushToWindowResize } from 'src/helpers/browser'

import styles from './RevealSection.module.scss'

const defaultProps = {
  showChildCount: 0, // number of child elements to render before hiding
  showHeight: null, // if provided, use as curtain's top positioning value
  curtainHeight: 80,
  shadowColor: 'none',
  buttonText: null,
  buttonBelow: false
}

const propTypes = {
  className: PropTypes.string,
  showChildCount: PropTypes.number,
  curtainHeight: PropTypes.number,
  shadowColor: PropTypes.string,
  buttonText: PropTypes.string,
  buttonBelow: PropTypes.bool,
  onReveal: PropTypes.func
}

const RevealButton = ({ typeClass, animate, revealed, buttonText }) => (
  <Button
    className={classnames(styles.button, typeClass, revealed && styles.fadeOut)}
    onClick={animate}
  >
    <If condition={buttonText}>
      <Text>{buttonText}</Text>
    </If>
    <If condition={!buttonText}>
      <FormattedMessage
        id='Transition.defualt.showMore'
        defaultMessage='Show more options'
      />
    </If>
  </Button>
)

const RevealSection = ({
  children,
  className,
  showHeight,
  showChildCount,
  curtainHeight,
  shadowColor,
  buttonText,
  buttonBelow,
  onReveal,
  skipReveal
}) => {
  const content = useRef(null)
  const positioner = useRef(null)
  const hash = useRef(
    Math.random()
      .toString(36)
      .substring(5)
  ).current

  const showCount = showChildCount || children.length
  const [top, setTop] = useState(showHeight)
  const [height, setHeight] = useState(0)
  const [revealed, setRevealed] = useState(false)

  const heightify = () => {
    if (!revealed && positioner.current) {
      const toTop =
        typeof showHeight === 'number'
          ? showHeight
          : positioner.current.offsetTop
      setTop(toTop)
      setHeight(toTop + curtainHeight)
    } else if (revealed && content.current) {
      setHeight(content.current.clientHeight)
    }
  }

  const animate = () => {
    setTop('100%')
    setHeight(content.current.clientHeight)
    setRevealed(true)
    if (onReveal) {
      onReveal()
    }
  }

  useEffect(() => {
    heightify()

    if (!revealed && skipReveal) {
      animate()
    }
  })

  pushToWindowResize(`RS_${hash}`, heightify)

  const sliceChildren = typeof children.slice === 'function'

  return (
    <>
      <div
        className={classnames(styles.wrapper, revealed && styles.transition)}
        style={{ height }}
      >
        <div className={classnames(className, styles.content)} ref={content}>
          {sliceChildren && children.slice(0, showCount)}
          {!sliceChildren &&
            React.cloneElement(children, {
              tabIndex: -1,
              className: classnames(
                !revealed && styles.forDisplayOnly,
                children.props && children.props.className
              )
            })}
          <div ref={positioner} className={styles.positioner} />
          <div
            className={classnames(
              styles.curtain,
              revealed && styles.transition,
              !revealed && styles.fadeSiblings
            )}
            style={{ top }}
          >
            <div
              className={classnames(
                styles.curtainShadow,
                styles[`curtainShadow-${shadowColor}`],
                revealed && styles.revealed
              )}
              style={{ height: curtainHeight }}
            >
              <If condition={!buttonBelow}>
                <RevealButton
                  typeClass={styles.backing}
                  animate={animate}
                  revealed={revealed}
                  buttonText={buttonText}
                />
              </If>
            </div>
          </div>
          {sliceChildren &&
            children.slice(showCount, children.length).map(child =>
              React.cloneElement(child, {
                tabIndex: -1,
                className: classnames(
                  !revealed && styles.forDisplayOnly,
                  child.props && child.props.className
                )
              })
            )}
        </div>
      </div>
      <If condition={buttonBelow}>
        <RevealButton
          typeClass={styles.center}
          animate={animate}
          revealed={revealed}
          buttonText={buttonText}
        />
      </If>
    </>
  )
}

RevealSection.defaultProps = defaultProps
RevealSection.propTypes = propTypes

export default RevealSection
